import { useState, useEffect } from "react";
import { z } from "zod";
import Select from "react-select";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import useLocalStorageState from "use-local-storage-state";
// Same source ProjectMeeting.jsx uses for its "Assign To" dropdown.
import useCommonData from "../../Utils/actions/advanceFilteringAction";
import { customStyles } from "../../Utils/helpers/commonhelper";
import { GetRequest } from "../../Utils/hooks/ApiHandler";

// Validation schema
const eventSchema = z
  .object({
    title: z.string().min(1, "Event title is required"),
    description: z.string().optional(),
    date: z.string().min(1, "Start date is required"),
    startTime: z.string().min(1, "Start time is required"),
    endDate: z.string().min(1, "End date is required"),
    endTime: z.string().min(1, "End time is required"),
    assignTo: z.string().optional(),
    // Invitees are now stored as full contact objects, not bare ids:
    // [{ id, email, fullName, module }]
    invitees: z
      .array(
        z.object({
          id: z.string(),
          email: z.string().optional(),
          fullName: z.string().optional(),
          module: z.string().optional(),
        })
      )
      .optional(),
  })
  .refine(
    (data) => {
      const start = new Date(`${data.date}T${data.startTime}`);
      const end = new Date(`${data.endDate}T${data.endTime}`);
      return start <= end;
    },
    {
      message: "Start date/time cannot be after end date/time",
      path: ["endDate"],
    }
  )
  .refine(
    (data) => {
      if (data.date === data.endDate) {
        return data.startTime < data.endTime;
      }
      return true;
    },
    {
      message: "Start time must be before end time if dates are same",
      path: ["endTime"],
    }
  );

export default function OutlookAddEventModal({
  isOpen,
  onClose,
  onSubmit,
  formData,
  setFormData,
  isEditing = false,
  customerId,
  headers,
  extras,
}) {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [customer] = useLocalStorageState("customer");

  const { leadOwner } = useCommonData({ leadOwner: true });
  const assigneeOptions = (leadOwner || []).map((lead) => ({
    value: lead.value,
    label: lead.label,
  }));

  const [emailOptions, setEmailOptions] = useState(null);
  const [isLoadingInvitees, setIsLoadingInvitees] = useState(false);
  const [inviteeSearchInput, setInviteeSearchInput] = useState("");
  const [debounceInviteeSearch, setDebounceInviteeSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceInviteeSearch(inviteeSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [inviteeSearchInput]);

  useEffect(() => {
    if (!isOpen || !customerId) return;
    (async () => {
      try {
        setIsLoadingInvitees(true);
        const res = await GetRequest(
          `lead/getAllEmailWithNameForMeeting/${customerId}?search=${debounceInviteeSearch}`,
          { headers },
          extras,
        );
        if (res?.status === "fail" || res?.success === false) {
          return;
        }
        if (res) setEmailOptions(res);
      } catch {
        // silent - invitee list is best-effort
      } finally {
        setIsLoadingInvitees(false);
      }
    })();
  }, [isOpen, customerId, debounceInviteeSearch]);
  const selectedInviteeIds = new Set((formData.invitees || []).map((inv) => inv.id));
  const selectedInviteeEmails = new Set(
    (formData.invitees || []).map((inv) => (inv.email || "").toLowerCase()),
  );
  const seenEmails = new Set();
  const inviteeOptions = Object.entries(emailOptions || {})
    .flatMap(([module, list]) =>
      (list || []).map((item) => ({
        value: String(item.id),
        label: item.fullName ? `${item.fullName} (${item.email})` : item.email,
        email: item.email,
        fullName: item.fullName,
        module,
      })),
    )
    .filter((opt) => {
      const emailKey = (opt.email || "").toLowerCase();
      if (selectedInviteeIds.has(opt.value) || selectedInviteeEmails.has(emailKey)) {
        return false;
      }
      if (emailKey && seenEmails.has(emailKey)) return false;
      if (emailKey) seenEmails.add(emailKey);
      return true;
    });
  const initialFormState = {
    title: "",
    description: "",
    date: "",
    startTime: "",
    endDate: "",
    endTime: "",
    assignTo: "",
    invitees: [],
  };

  useEffect(() => {
    if (isOpen && !isEditing && !formData.assignTo && customer?.customer_details?.id) {
      setFormData((prev) => ({ ...prev, assignTo: customer.customer_details.id }));
    }
  }, [isOpen, isEditing]);

  const handleClose = () => {
    setFormData(initialFormState);
    setErrors({});
    setInviteeSearchInput("");
    setDebounceInviteeSearch("");
    setEmailOptions(null);
    onClose();
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setErrors({});
    const result = eventSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors = {};
      result.error.errors.forEach((err) => {
        fieldErrors[err.path[0]] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      await onSubmit();
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const selectedAssignee =
    assigneeOptions.find((opt) => opt.value === formData.assignTo) || null;

  const selectedInvitees = (formData.invitees || []).map((inv) => ({
    value: inv.id,
    label: inv.fullName ? `${inv.fullName} (${inv.email})` : inv.email,
    email: inv.email,
    fullName: inv.fullName,
    module: inv.module,
  }));

  return (
    <Modal show={isOpen} onHide={handleClose}  size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title as="h5" className="fw-semibold fs-6 m-0">
          {isEditing ? "Edit Event" : "Add Event"}
        </Modal.Title>
      </Modal.Header>

      <Form  className="form-container" onSubmit={handleSave}>
        <Modal.Body>
          {/* Title */}
          <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventTitle">
            <Form.Label>
              Event title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Event Title"
              value={formData.title || ""}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              isInvalid={!!errors.title}
            />
            <Form.Control.Feedback type="invalid">
              {errors.title}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Description"
              value={formData.description || ""}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
          </Form.Group>

          {/* Start Date & Start Time */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventStartDate">
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.date || ""}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  isInvalid={!!errors.date}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.date}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventStartTime">
                <Form.Label>Start Time</Form.Label>
                <Form.Control
                  type="time"
                  value={formData.startTime || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, startTime: e.target.value })
                  }
                  isInvalid={!!errors.startTime}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.startTime}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* End Date & End Time */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventEndDate">
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.endDate || formData.date || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, endDate: e.target.value })
                  }
                  isInvalid={!!errors.endDate}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.endDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventEndTime">
                <Form.Label>End Time</Form.Label>
                <Form.Control
                  type="time"
                  value={formData.endTime || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, endTime: e.target.value })
                  }
                  isInvalid={!!errors.endTime}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.endTime}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* Assign To */}
          <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventAssignTo">
            <Form.Label>Assign To</Form.Label>
            <Select
              options={assigneeOptions}
              value={selectedAssignee}
              styles={customStyles}
              onChange={(selectedOption) =>
                setFormData({
                  ...formData,
                  assignTo: selectedOption?.value || "",
                })
              }
              isClearable
              classNamePrefix="react-select"
              placeholder="Select assignee..."
            />
            {errors.assignTo && (
              <div className="text-danger small mt-1">{errors.assignTo}</div>
            )}
          </Form.Group>

          {/* Invitees */}
          <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative " controlId="eventInvitees">
            <Form.Label>Invitees</Form.Label>
            <Select
              options={inviteeOptions}
              value={selectedInvitees}
              styles={customStyles}
              isMulti
              hideSelectedOptions
              isLoading={isLoadingInvitees}
              onInputChange={(value, meta) => {
                if (meta.action === "input-change") {
                  setInviteeSearchInput(value);
                }
              }}
              onChange={(selectedOptions) =>
                setFormData({
                  ...formData,
                  // Store the full invitee object, matching how it's saved
                  // to the DB: { id, email, fullName, module }
                  invitees: (selectedOptions || []).map((opt) => ({
                    id: opt.value,
                    email: opt.email,
                    fullName: opt.fullName,
                    module: opt.module,
                  })),
                })
              }
              isClearable
              classNamePrefix="react-select"
              placeholder="Select invitees..."
            />
            {errors.invitees && (
              <div className="text-danger small mt-1">{errors.invitees}</div>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button className="btn btn-default-outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button className="btn btn-default" type="submit" disabled={loading}>
            {loading && (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
            )}
            {loading ? "Saving..." : isEditing ? "Update Event" : "Save Event"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}